import { z } from 'zod';
import { userRoleSchema } from './enums.js';
import { booleanQueryParam } from './query-params.js';
/**
 * Password policy.
 *
 * Length is the requirement that actually matters; composition rules mostly
 * push people towards `Passwort1!` and a sticky note. Twelve characters is the
 * OWASP floor for a system without a second factor.
 */
export const passwordSchema = z
    .string()
    .min(12, 'Das Passwort muss mindestens 12 Zeichen lang sein')
    .max(200, 'Das Passwort darf höchstens 200 Zeichen lang sein');
/**
 * Normalisation runs *before* validation: a pasted address with a trailing
 * space is a typo, not an invalid address. Stored and compared in lower case,
 * because nobody expects Anna@… and anna@… to be two accounts.
 */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email('Bitte eine gültige E-Mail-Adresse angeben').max(320));
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1, 'Bitte das Passwort eingeben'),
});
/**
 * The user as any client may see it. Never contains the password hash.
 */
export const publicUserSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    name: z.string(),
    role: userRoleSchema,
    /** Entry in the `buyers` value list used to pre-fill the Buyer field. */
    buyerItemId: z.uuid().nullable(),
    isActive: z.boolean(),
    lastLoginAt: z.iso.datetime({ offset: true }).nullable(),
    createdAt: z.iso.datetime({ offset: true }),
});
/**
 * Login and refresh response.
 *
 * `refreshToken` is only populated for clients that cannot hold a cookie — the
 * iPhone app, which puts it in the Keychain. Browsers get it as an httpOnly
 * cookie and the field stays null, so a successful XSS still cannot read the
 * long-lived credential.
 */
export const authTokensSchema = z.object({
    accessToken: z.string(),
    /** Seconds until the access token expires. */
    expiresIn: z.number().int().positive(),
    tokenType: z.literal('Bearer'),
    refreshToken: z.string().nullable(),
    user: publicUserSchema,
});
/** Body of the refresh call. Empty for browsers, which send the cookie instead. */
export const refreshRequestSchema = z.object({
    refreshToken: z.string().min(1).optional(),
});
/** Claims carried by an access token. */
export const accessTokenClaimsSchema = z.object({
    /** Subject: the user id. */
    sub: z.uuid(),
    role: userRoleSchema,
    /** Token type, so a refresh token cannot be presented as an access token. */
    typ: z.literal('access'),
    iat: z.number().int(),
    exp: z.number().int(),
});
// --- User administration (admin only) ---------------------------------------
export const userCreateSchema = z.object({
    email: emailSchema,
    name: z.string().min(1, 'Bitte einen Namen angeben').max(200),
    password: passwordSchema,
    role: userRoleSchema.default('capturer'),
    buyerItemId: z.uuid().nullable().default(null),
});
/**
 * What an admin may change on an existing account.
 *
 * The email is deliberately immutable: it identifies the account across the
 * audit log and every captured item's authorship. Renaming a person is a name
 * change, not an identity change.
 */
export const userUpdateSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    role: userRoleSchema.optional(),
    isActive: z.boolean().optional(),
    buyerItemId: z.uuid().nullable().optional(),
})
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Es wurde keine Änderung angegeben',
});
export const passwordResetSchema = z.object({
    password: passwordSchema,
    /**
     * Whether every existing session of that user is terminated. Defaults to
     * true: a password reset is usually a response to something having gone
     * wrong, and leaving old sessions alive would defeat the point.
     */
    revokeSessions: z.boolean().default(true),
});
/** Changing one's own password requires proving knowledge of the current one. */
export const passwordChangeSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
});
export const userListQuerySchema = z.object({
    /** Substring match against name and email. */
    search: z.string().max(200).optional(),
    role: userRoleSchema.optional(),
    includeInactive: booleanQueryParam.default(false),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const userListResponseSchema = z.object({
    users: z.array(publicUserSchema),
    total: z.number().int().min(0),
});
/** One active or past refresh-token chain, shown in the session overview. */
export const sessionSchema = z.object({
    id: z.uuid(),
    familyId: z.uuid(),
    userAgent: z.string().nullable(),
    ipAddress: z.string().nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    expiresAt: z.iso.datetime({ offset: true }),
    isCurrent: z.boolean(),
});
//# sourceMappingURL=auth.js.map